import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation30() {
  const { lesson, course, accent } = findLesson("piano-foundation", "30");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Walk through the <strong>seven triads</strong> of any major scale (1-4-5 are major, 2-3-6 are minor, 7 is diminished) ascending and descending, with chord patterns.</Highlight>
        <Highlight>Three positions for every triad: <strong>root</strong> (root on bottom), <strong>first inversion</strong> (root on top), <strong>second inversion</strong> (root in the middle).</Highlight>
        <Highlight>Watch the root: in <strong>root position</strong> it's on the left; in <strong>first inversion</strong> it's on the right; in <strong>second inversion</strong> it's in the middle.</Highlight>
        <Highlight>Three reusable RH patterns to play through the triads — <strong>1:4 block</strong>, <strong>broken chord</strong>, <strong>arpeggio LMHM</strong>.</Highlight>
        <Highlight>Avoid the pinky for the top note when ascending — it forces an awkward cross. Use the ring finger only when the top note is a black key.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Setup" title="The seven triads of G major" accent={accent.hex}>
        <p>
          Pick a key — the chord is <strong>G major</strong>. Build a triad on every note
          of the scale by stacking thirds (skip-skip on the scale circle):
        </p>

        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[13px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Degree</th>
                <th>Triad</th>
                <th>Quality</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-nsm-border"><td>1</td><td>G - B - D</td><td className="text-emerald-300">G major</td></tr>
              <tr className="border-t border-nsm-border"><td>2</td><td>A - C - E</td><td className="text-rose-300">A minor</td></tr>
              <tr className="border-t border-nsm-border"><td>3</td><td>B - D - F♯</td><td className="text-rose-300">B minor</td></tr>
              <tr className="border-t border-nsm-border"><td>4</td><td>C - E - G</td><td className="text-emerald-300">C major</td></tr>
              <tr className="border-t border-nsm-border"><td>5</td><td>D - F♯ - A</td><td className="text-emerald-300">D major</td></tr>
              <tr className="border-t border-nsm-border"><td>6</td><td>E - G - B</td><td className="text-rose-300">E minor</td></tr>
              <tr className="border-t border-nsm-border"><td>7</td><td>F♯ - A - C</td><td className="text-amber-300">F♯ diminished</td></tr>
            </tbody>
          </table>
        </div>

        <Vocab>
          <Term name="Root position" def="root note on the bottom. G-B-D for G major. The chord's natural shape." />
          <Term name="First inversion" def="root jumps to the top. B-D-G for G major. Root is now on the right." />
          <Term name="Second inversion" def="root sits in the middle. D-G-B for G major." />
          <Term name="Hand ratio (1:4)" def="four right-hand chord hits per single left-hand bass — the pulse for this drill." />
        </Vocab>
      </Insight>

      <Insight eyebrow="Where the root lives" title="Three positions, three places for the root" accent={accent.hex}>
        <Pull>
          Root position, root left. First inversion, root is on the right, and second
          inversion, root stays in the centre.
        </Pull>

        <p>
          That's the visual rule for fingering. In root position, your <em>thumb</em> plays
          the root. In first inversion, your <em>pinky</em> plays the root. In second
          inversion, your <em>middle finger</em> plays the root. Same chord, three different
          finger ergonomics.
        </p>

        <Tip>
          Write each chord <strong>in a circle</strong>, not a line. The circular layout
          makes the three positions obvious — start at any of the three notes and go
          clockwise, you've got that inversion.
        </Tip>
      </Insight>

      <Insight eyebrow="Drill 1 · 1:4 block chords" title="Walk through all seven triads, root position" accent={accent.hex}>
        <p>
          The simplest drill: play each triad four times in the right hand, with one bass
          note in the left hand per change. That's a <strong>1:4 hand ratio</strong>. Walk
          up the seven triads of the scale, then walk back down.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          G (×4) - Am (×4) - Bm (×4) - C (×4) - D (×4) - Em (×4) - F♯dim (×4) - G (×4)
        </p>

        <Tip>
          Avoid using your pinky on the top note when climbing — a pinky-on-top makes the
          next jump awkward. Use the <em>ring finger</em> on the top whenever the top note
          is black; otherwise use the middle.
        </Tip>
      </Insight>

      <Insight eyebrow="Drill 2 · Broken chords" title="Top two together, then bottom" accent={accent.hex}>
        <p>
          Once the block-chord drill is comfortable, switch to <strong>broken chords</strong>{" "}
          — the same triads but split into two halves: top two notes together, then the
          bottom note alone. Walk up and down the scale playing every chord this way.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          G major broken: B-D together · G alone · B-D · G · …
        </p>
      </Insight>

      <Insight eyebrow="Drill 3 · Arpeggios (LMHM)" title="Each triad as a four-note arpeggio" accent={accent.hex}>
        <p>
          Then break the chord all the way: low-middle-high-middle. Same seven triads,
          arpeggiated. For G major: G-B-D-B. For A minor: A-C-E-C. And so on up the scale.
        </p>
      </Insight>

      <Insight eyebrow="Now the inversions" title="Same seven chords, all in first inversion" accent={accent.hex}>
        <p>
          Re-walk the scale — but this time every chord is in <strong>first inversion</strong>.
          Same chords, different shape. G major becomes B-D-G. A minor becomes C-E-A. B minor
          becomes D-F♯-B. And so on.
        </p>

        <p>
          Then re-walk again with every chord in <strong>second inversion</strong>: D-G-B,
          E-A-C, F♯-B-D, etc.
        </p>

        <KeyIdea>
          Same emotion, different texture. The chord quality (major / minor) doesn't change
          when you invert — only the colour does. Your ear will start to recognise the
          difference between "root position" and "first inversion" as a flavour, not a
          theoretical concept.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Practice path" title="One key, three patterns, three positions" accent={accent.hex}>
        <Steps>
          <Step>Pick G major. Write the seven triads.</Step>
          <Step>Play all seven in root position with the 1:4 block-chord drill, ascending and descending.</Step>
          <Step>Switch the right-hand pattern to broken chords. Same seven triads.</Step>
          <Step>Switch to LMHM arpeggios. Same seven triads.</Step>
          <Step>Now move to first inversion of every triad. Repeat the three patterns.</Step>
          <Step>Move to second inversion. Repeat the three patterns.</Step>
          <Step>Switch keys — F major, B♭ major, C major. The exact same drill, transposed.</Step>
        </Steps>

        <Tip>
          Sing the root in the bass while your right hand walks the chords. It locks the
          chord-quality recognition (major-minor-minor-major-major-minor-diminished) into
          your ear. Lesson 31 will make this drill more musical by reorganising it around
          chord <em>quality</em> instead of scale-walking.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-31"
        label="Lesson 31 · Chord Inversion Practice (Part 2)"
        why="The Part 1 drill is linear — every triad in scale order. Part 2 reorganises by chord quality and turns the inversion drill into something more musical."
      />
    </LessonShell>
  );
}
